import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileStack,
  Trash2,
  Copy,
  Archive,
  Search,
  Plus,
  FileText,
  Pencil,
  UploadCloud,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const COMMON_CATEGORIES = [
  "Offer letters",
  "NDAs",
  "Service agreements",
  "HR forms",
  "Vendor agreements",
];

export default function Templates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [newBusy, setNewBusy] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/templates", {
        params: { category: category === "all" ? undefined : category, archived, q: q || undefined },
      });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [category, archived]);

  const archive = async (id, value) => {
    try {
      await api.patch(`/templates/${id}`, { archived: value });
      toast.success(value ? "Archived" : "Restored");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/templates/${id}/duplicate`);
      toast.success("Duplicated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this template?")) return;
    try {
      await api.delete(`/templates/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleUseTemplate = async (t) => {
    try {
      const { data } = await api.post(`/templates/${t.id}/use`, {});
      toast.success("Created from template");
      navigate(`/app/documents/${data.id}/prepare`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const fileToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // 1×1 transparent PDF used for "blank" templates so the prepare canvas
  // has something to render until the admin replaces it.
  const BLANK_PDF_DATA_URL =
    "data:application/pdf;base64,JVBERi0xLjQKJcOkw7zDtsOfCjIgMCBvYmoKPDwvTGVuZ3RoIDMgMCBSL0ZpbHRlci9GbGF0ZURlY29kZT4+CnN0cmVhbQp4nDPUMwAAPQBkAAplbmRzdHJlYW0KZW5kb2JqCjMgMCBvYmoKMTMKZW5kb2JqCjEgMCBvYmoKPDwvVHlwZS9QYWdlL1BhcmVudCA0IDAgUi9NZWRpYUJveFswIDAgNTk1IDg0Ml0vUmVzb3VyY2VzPDw+Pi9Db250ZW50cyAyIDAgUj4+CmVuZG9iago0IDAgb2JqCjw8L1R5cGUvUGFnZXMvQ291bnQgMS9LaWRzWzEgMCBSXT4+CmVuZG9iago1IDAgb2JqCjw8L1R5cGUvQ2F0YWxvZy9QYWdlcyA0IDAgUj4+CmVuZG9iagp4cmVmCjAgNgowMDAwMDAwMDAwIDY1NTM1IGYgCjAwMDAwMDAwODYgMDAwMDAgbiAKMDAwMDAwMDAxNSAwMDAwMCBuIAowMDAwMDAwMDY5IDAwMDAwIG4gCjAwMDAwMDAxNjAgMDAwMDAgbiAKMDAwMDAwMDIwOCAwMDAwMCBuIAp0cmFpbGVyCjw8L1NpemUgNi9Sb290IDUgMCBSPj4Kc3RhcnR4cmVmCjI1MwolJUVPRgo=";

  const resetNew = () => {
    setNewTitle("");
    setNewDescription("");
    setNewCategory("");
    setNewFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const createTemplate = async () => {
    if (!newTitle.trim()) return toast.error("Template name is required.");
    setNewBusy(true);
    try {
      let payload = {
        title: newTitle.trim(),
        description: newDescription.trim() || null,
        category: newCategory || null,
        file_name: "blank.pdf",
        file_data_url: BLANK_PDF_DATA_URL,
        page_count: 1,
        fields: [],
        roles: ["Signer"],
        default_recipients: [],
      };
      if (newFile) {
        const dataUrl = await fileToDataUrl(newFile);
        payload = {
          ...payload,
          file_name: newFile.name,
          file_data_url: dataUrl,
        };
      }
      const { data } = await api.post("/templates", payload);
      toast.success(newFile ? "Template uploaded" : "Blank template created");
      setNewOpen(false);
      resetNew();
      navigate(`/app/templates/${data.id}/edit`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNewBusy(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl mx-auto" data-testid="templates-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-medium">
            Templates
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Reusable templates</h1>
          <p className="mt-1 text-sm text-slate-500">
            Pre-prepared documents — one click to send.
          </p>
        </div>
        <Button
          onClick={() => setNewOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="new-template-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New template
        </Button>
      </div>

      <div className="mt-8 flex items-center gap-3 flex-wrap">
        <Tabs value={category} onValueChange={setCategory}>
          <TabsList>
            <TabsTrigger value="all" data-testid="cat-all">All</TabsTrigger>
            {COMMON_CATEGORIES.map((c) => (
              <TabsTrigger key={c} value={c} data-testid={`cat-${c}`}>
                {c}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Button
          variant={archived ? "default" : "outline"}
          size="sm"
          onClick={() => setArchived(!archived)}
          data-testid="toggle-archived"
          className={archived ? "bg-slate-900 hover:bg-slate-800 text-white" : ""}
        >
          <Archive className="h-3.5 w-3.5 mr-1" />
          {archived ? "Showing archived" : "Show archived"}
        </Button>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
          className="relative ml-auto min-w-[240px]"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search templates…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10"
            data-testid="templates-search"
          />
        </form>
      </div>

      <div className="mt-6">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-16 text-center">
            <FileStack className="h-10 w-10 text-slate-400 mx-auto" />
            <div className="mt-4 text-base font-medium">
              {archived ? "No archived templates" : "No templates yet"}
            </div>
            <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              Save your most-used documents (offer letters, NDAs, vendor contracts) as
              templates and send them in one click.
            </div>
            {!archived && (
              <Button
                onClick={() => setNewOpen(true)}
                className="mt-5 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="empty-new-template-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Create your first template
              </Button>
            )}
          </div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <div
              key={t.id}
              className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:shadow-sm transition flex flex-col"
              data-testid={`template-card-${t.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="h-9 w-9 rounded-md bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                  <FileStack className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500">
                      <span className="text-lg leading-none">⋯</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => navigate(`/app/templates/${t.id}/edit`)}
                      data-testid={`tpl-edit-${t.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => duplicate(t.id)} data-testid={`tpl-duplicate-${t.id}`}>
                      <Copy className="h-3.5 w-3.5 mr-2" /> Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => archive(t.id, !t.archived)} data-testid={`tpl-archive-${t.id}`}>
                      <Archive className="h-3.5 w-3.5 mr-2" />
                      {t.archived ? "Restore" : "Archive"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => remove(t.id)} className="text-red-600">
                      <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <h3 className="mt-3 font-semibold">{t.title}</h3>
              {t.category && (
                <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 self-start">
                  {t.category}
                </div>
              )}
              <p className="text-xs text-slate-500 mt-2 line-clamp-2">
                {t.description || "No description"}
              </p>
              <div className="mt-4 flex items-center gap-3 text-xs text-slate-500 font-mono">
                <span>{t.fields?.length || 0} fields</span>
                <span>·</span>
                <span>{t.page_count} pages</span>
                <span>·</span>
                <span>used {t.usage_count}×</span>
              </div>
              <div className="mt-4 flex items-center gap-2">
                <Button
                  onClick={() => handleUseTemplate(t)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid={`tpl-use-${t.id}`}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Use template
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/app/templates/${t.id}/edit`)}
                  data-testid={`tpl-edit-card-${t.id}`}
                  title="Edit fields"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* New template dialog */}
      <Dialog
        open={newOpen}
        onOpenChange={(o) => {
          setNewOpen(o);
          if (!o) resetNew();
        }}
      >
        <DialogContent className="max-w-lg" key={newOpen ? "open" : "closed"}>
          <DialogHeader>
            <DialogTitle>Create a template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="tpl-title">Name</Label>
              <Input
                id="tpl-title"
                autoFocus
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="e.g. Standard NDA"
                className="mt-1.5 h-10"
                data-testid="new-tpl-title"
              />
            </div>
            <div>
              <Label htmlFor="tpl-desc">Description</Label>
              <Textarea
                id="tpl-desc"
                rows={2}
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="What is this template used for?"
                className="mt-1.5"
                data-testid="new-tpl-desc"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger className="mt-1.5 h-10" data-testid="new-tpl-cat">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Document</Label>
              <div className="mt-1.5 rounded-md border border-dashed border-slate-300 p-4 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => setNewFile(e.target.files?.[0] || null)}
                  data-testid="new-tpl-file-input"
                />
                {newFile ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm min-w-0">
                      <FileText className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span className="truncate">{newFile.name}</span>
                    </div>
                    <button
                      onClick={() => {
                        setNewFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="text-xs text-slate-500 hover:text-slate-900 underline"
                      type="button"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <>
                    <UploadCloud className="h-6 w-6 text-slate-400 mx-auto" />
                    <div className="mt-2 text-xs text-slate-600">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="font-medium text-emerald-700 hover:underline"
                        data-testid="new-tpl-pick-file"
                      >
                        Upload a PDF
                      </button>
                      <span className="text-slate-400">
                        {" "}
                        or leave empty and start from scratch
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={createTemplate}
              disabled={newBusy || !newTitle.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="new-tpl-create"
            >
              {newBusy
                ? "Creating…"
                : newFile
                  ? "Upload & continue"
                  : "Create blank template"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
